import { CoreEnums } from '@/utils/domain/constants';

const { ChatMessageStatus } = CoreEnums;

const POLICY_VIOLATION_STATUS_STRINGS = new Set([
  'policy-violation',
  'policy_violation',
  'policyviolation',
  'policy violation',
]);

const FAILED_STATUS_STRINGS = new Set([
  'failed',
  'fail',
  'message_failed',
  'message-failed',
  'delivery_failed',
  'delivery-failed',
]);

const ERROR_STATUS_STRINGS = new Set(['error', 'message_error', 'message-error', 'general-error']);

export const SELF_USER_MESSAGE_ACK_DELAY_MS = 250;

export const getFirstDefinedValue = (...candidates) => {
  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (candidate === undefined || candidate === null || candidate === '') {
      continue;
    }
    return candidate;
  }
  return null;
};

const flattenErrorMessages = (collection) => {
  if (!Array.isArray(collection)) {
    return '';
  }
  return collection
    .map((entry) => {
      if (!entry) return '';
      if (typeof entry === 'string') return entry;
      if (typeof entry === 'object' && entry.message) return entry.message;
      return String(entry);
    })
    .filter((entry) => entry && entry.trim())
    .join('\n')
    .trim();
};

export const toStatusString = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized || null;
  }
  if (typeof value === 'number') {
    if (ChatMessageStatus) {
      if (value === ChatMessageStatus.Failed) return 'failed';
      if (value === ChatMessageStatus.Error) return 'error';
      if (value === ChatMessageStatus.PolicyViolation) return 'policy-violation';
    }
    return String(value);
  }
  return null;
};

const matchesStatusValue = (status, numericTarget, stringSet) => {
  if (status === undefined || status === null) return false;

  const numeric = Number(status);
  if (!Number.isNaN(numeric) && typeof numericTarget === 'number' && numeric === numericTarget) {
    return true;
  }

  const stringStatus = String(status).trim().toLowerCase();
  if (!stringStatus) return false;

  return stringSet.has(stringStatus);
};

export const isFailedStatusValue = (status) =>
  matchesStatusValue(status, ChatMessageStatus?.Failed, FAILED_STATUS_STRINGS);

export const isErrorStatusValue = (status) =>
  matchesStatusValue(status, ChatMessageStatus?.Error, ERROR_STATUS_STRINGS);

export const isPolicyViolationStatusValue = (status) => {
  if (status === undefined || status === null) return false;

  const numeric = Number(status);
  if (!Number.isNaN(numeric)) {
    return ChatMessageStatus && numeric === ChatMessageStatus.PolicyViolation;
  }

  const normalized = String(status).trim().toLowerCase();
  if (!normalized) return false;

  return POLICY_VIOLATION_STATUS_STRINGS.has(normalized);
};

export const getMessageIdentityKey = (payload) => {
  if (!payload) return null;
  const candidates = [
    payload.messageId,
    payload.id,
    payload.channelMessageIdentifier,
    payload?.content?.messageId,
  ];
  const found = candidates.find(
    (candidate) => candidate !== undefined && candidate !== null && candidate !== '',
  );
  return found ? String(found).toLowerCase() : null;
};

const isSameMessageId = (candidate, target) => {
  if (!candidate || !target) return false;
  return String(candidate).toLowerCase() === String(target).toLowerCase();
};

export const findMessageBySocketId = (messagesCollection, payload) => {
  if (!messagesCollection || !payload) return null;
  const possibleIds = [
    payload.messageId,
    payload.id,
    payload.channelMessageIdentifier,
    payload.content?.messageId,
  ].filter(Boolean);

  if (possibleIds.length === 0) return null;

  return messagesCollection.find((message) => {
    const messageCandidates = [
      message?.messageId,
      message?.id,
      message?.channelMessageIdentifier,
      message?.content?.messageId,
    ].filter(Boolean);

    if (messageCandidates.length === 0) return false;

    return possibleIds.some((incomingId) =>
      messageCandidates.some((existingId) => isSameMessageId(existingId, incomingId)),
    );
  });
};

export const enrichSocketMessagePayload = (incoming) => {
  if (!incoming || typeof incoming !== 'object') return;

  const resolvedStatus = getFirstDefinedValue(
    incoming.status,
    incoming.messageStatus,
    incoming.chatMessageStatus,
    incoming.data?.status,
    incoming.content?.status,
  );

  const statusString = toStatusString(resolvedStatus);

  let normalizedContent =
    incoming.content && typeof incoming.content === 'object' ? { ...incoming.content } : null;

  if (resolvedStatus !== null && resolvedStatus !== undefined) {
    incoming.status = resolvedStatus;
    if (
      incoming.messageStatus === undefined ||
      incoming.messageStatus === null ||
      incoming.messageStatus === ''
    ) {
      incoming.messageStatus = resolvedStatus;
    }
    if (
      incoming.chatMessageStatus === undefined ||
      incoming.chatMessageStatus === null ||
      incoming.chatMessageStatus === ''
    ) {
      incoming.chatMessageStatus = resolvedStatus;
    }
  }

  if (!normalizedContent && incoming.content && typeof incoming.content === 'object') {
    normalizedContent = { ...incoming.content };
  }

  if (normalizedContent) {
    if (
      normalizedContent.status === undefined ||
      normalizedContent.status === null ||
      normalizedContent.status === ''
    ) {
      if (statusString) {
        normalizedContent.status = statusString;
      } else if (resolvedStatus !== null && resolvedStatus !== undefined) {
        normalizedContent.status = resolvedStatus;
      }
    }
  }

  const resolvedErrorMessage =
    incoming.errorMessage ||
    incoming.data?.errorMessage ||
    incoming.content?.errorMessage ||
    flattenErrorMessages(incoming.errorMessages) ||
    flattenErrorMessages(incoming.errors) ||
    flattenErrorMessages(incoming.content?.errorMessages) ||
    flattenErrorMessages(incoming.content?.errors) ||
    flattenErrorMessages(incoming.data?.errorMessages) ||
    flattenErrorMessages(incoming.data?.errors);

  if (resolvedErrorMessage) {
    incoming.errorMessage = resolvedErrorMessage;
    if (normalizedContent && !normalizedContent.errorMessage) {
      normalizedContent.errorMessage = resolvedErrorMessage;
    }
  }

  if (normalizedContent) {
    incoming.content = normalizedContent;
  }
};

const LLM_GUARDRAIL_DECISION_KEYS = [
  'jailbreak',
  'customPrompt',
  'promptInjection',
  'hallucination',
];

export const readGuardrailsNode = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return payload.content?.guardrails || payload.guardrails || null;
};

/**
 * Canonical decision: flagged && confidence >= threshold.
 * An omitted threshold on a canonical decision is 0, not 0.7.
 * Legacy envelopes without `flagged` are not tripwires here.
 */
export const isLlmTripwire = (decision) => {
  if (!decision || typeof decision !== 'object' || decision.flagged !== true) return false;
  if (typeof decision.confidence !== 'number' || !Number.isFinite(decision.confidence))
    return false;
  const threshold =
    typeof decision.threshold === 'number' && Number.isFinite(decision.threshold)
      ? decision.threshold
      : 0;
  return decision.confidence >= threshold;
};

export const isGuardrailPolicyBlock = (guardrails) => {
  if (!guardrails || typeof guardrails !== 'object') return false;
  if (String(guardrails.status || '').toLowerCase() === 'policy-violation') return true;
  if (guardrails.moderation?.allowed === false) return true;
  if (guardrails.urlFilter?.allowed === false) return true;

  const jailbreak = guardrails.jailbreak;
  if (jailbreak?.allowed === false) return true;
  if (jailbreak?.jailbreak?.isJailbreak === true) return true;
  if (jailbreak?.promptInjection?.isInjection === true) return true;

  return LLM_GUARDRAIL_DECISION_KEYS.some((key) => isLlmTripwire(guardrails[key]));
};

export const hasPermissiveGuardrailFailure = (guardrails) => {
  if (!guardrails || typeof guardrails !== 'object') return false;
  const status = String(guardrails.status || '').toLowerCase();
  if (status === 'policy-violation' || status === 'error') return false;
  if (guardrails.ok === false) return false;
  return (
    Array.isArray(guardrails.evaluations) && guardrails.evaluations.some((row) => row && row.error)
  );
};

const AUDIT_LABELS = {
  jailbreak: {
    input: 'supportChat.guardrails.audit.jailbreak.input',
  },
  customPrompt: {
    input: 'supportChat.guardrails.audit.customPrompt.input',
  },
  promptInjection: {
    output: 'supportChat.guardrails.audit.promptInjection.output',
    pre_flight: 'supportChat.guardrails.audit.promptInjection.preFlight',
  },
  hallucination: {
    delivery: 'supportChat.guardrails.audit.hallucination.delivery',
  },
};

export const listGuardrailEvaluations = (guardrails) => {
  if (!Array.isArray(guardrails?.evaluations)) return [];
  return guardrails.evaluations
    .filter((row) => row && row.check)
    .map((row) => {
      const stage = String(row.stage || '');
      return {
        key: `${row.check}-${stage}-${row.batch || 0}`,
        labelId: AUDIT_LABELS[row.check]?.[stage] || 'supportChat.guardrails.audit.generic',
        check: row.check,
        stage,
        batch: row.batch || 0,
        error: row.error || null,
      };
    });
};
